import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { TreePine, Car, Plane, Clock } from 'lucide-react';

/* ── Coordinates ── */
const GOLF_SONGON   = [5.35, -4.27];
const LE_PLATEAU    = [5.322, -4.017];
const YOPOUGON_G    = [5.355, -4.10];
const YOPOUGON_S    = [5.335, -4.065];
const JACQUEVILLE   = [5.21, -4.41];
const AIRPORT_FHB   = [5.261, -3.928];
const MAP_CENTER    = [5.32, -4.15];

/* ── Marker Icons ── */
const golfIcon = L.divIcon({
  className: '',
  iconSize: [48, 48],
  iconAnchor: [24, 24],
  html: `
    <div style="width:48px;height:48px;display:flex;align-items:center;justify-content:center">
      <div style="width:40px;height:40px;border-radius:50%;background:#1a3c2a;border:3px solid #c9a962;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 15px rgba(0,0,0,0.25),0 0 0 6px rgba(26,60,42,0.15)">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#c9a962" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/>
        </svg>
      </div>
    </div>
  `
});

const bluePin = L.divIcon({
  className: '',
  iconSize: [28, 36],
  iconAnchor: [14, 36],
  html: `
    <svg width="28" height="36" viewBox="0 0 28 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 0C6.27 0 0 6.27 0 14c0 10.5 14 22 14 22s14-11.5 14-22C28 6.27 21.73 0 14 0z" fill="#c9a962"/>
      <circle cx="14" cy="13" r="5.5" fill="#fff"/>
    </svg>
  `
});

/* ── Travel Time Data ── */
const destinations = [
  {
    id: 'plateau',
    name: 'Abidjan (Le Plateau)',
    route: 'Via la voie express',
    time: '~30 min',
    icon: Car,
    coords: LE_PLATEAU,
  },
  {
    id: 'yopougon-gesco',
    name: 'Yopougon (Gesco)',
    route: 'Route directe',
    time: '~12 min',
    icon: Car,
    coords: YOPOUGON_G,
  },
  {
    id: 'yopougon-siporex',
    name: 'Yopougon (Siporex)',
    route: 'Via carrefour',
    time: '~25 min',
    icon: Car,
    coords: YOPOUGON_S,
  },
  {
    id: 'jacqueville',
    name: 'Carrefour Jacqueville',
    route: 'Route principale',
    time: '~5 min',
    icon: Car,
    coords: JACQUEVILLE,
  },
  {
    id: 'airport',
    name: 'Aéroport FHB',
    route: '4ème pont & autoroute',
    time: '~50 min',
    icon: Plane,
    coords: AIRPORT_FHB,
  },
];

/* ── FlyTo on hover ── */
const FlyToMarker = ({ coords }) => {
  const map = useMap();
  if (coords) {
    map.flyTo(coords, 13, { duration: 0.8 });
  } else {
    map.flyTo(GOLF_SONGON, 11, { duration: 0.8 });
  }
  return null;
};

/* ── Component ── */
const GolfAccessibilite = () => {
  const [hoveredId, setHoveredId] = useState(null);
  const hoveredCoords = destinations.find(d => d.id === hoveredId)?.coords || null;

  return (
    <section className="py-24 lg:py-32 bg-[#0f2419] relative overflow-hidden" data-testid="accessibilite-section">
      <style>{`
        .leaflet-control-zoom { border: none !important; border-radius: 10px !important; overflow: hidden; box-shadow: 0 2px 12px rgba(0,0,0,0.12) !important; }
        .leaflet-control-zoom a { width: 34px !important; height: 34px !important; line-height: 34px !important; font-size: 16px !important; color: #1a3c2a !important; border-bottom: 1px solid #eee !important; }
        .leaflet-control-zoom a:hover { background: #f8f6f1 !important; }
        .leaflet-control-attribution { display: none !important; }
      `}</style>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="font-montserrat text-[#c9a962] text-xs tracking-[0.4em] uppercase">
            Situation & Accessibilité
          </span>
          <div className="w-16 h-px bg-[#c9a962] mx-auto my-5" />
          <h2 className="font-playfair text-4xl lg:text-5xl text-white leading-tight">
            Un sanctuaire verdoyant
            <span className="block text-[#c9a962] italic">aux portes du Grand Abidjan</span>
          </h2>
        </div>

        {/* Split Layout: Map + Travel Times */}
        <div className="grid lg:grid-cols-[1fr_380px] gap-0 rounded-2xl overflow-hidden shadow-2xl border border-white/[0.06] mb-14">
          {/* Left: Map */}
          <div className="relative h-[450px] lg:h-[560px]">
            <MapContainer
              center={MAP_CENTER}
              zoom={11}
              scrollWheelZoom={false}
              dragging={true}
              zoomControl={true}
              attributionControl={false}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FlyToMarker coords={hoveredCoords} />

              {/* Golf Marker */}
              <Marker position={GOLF_SONGON} icon={golfIcon} />

              {/* Destination Markers */}
              {destinations.map(d => (
                <Marker key={d.id} position={d.coords} icon={bluePin} />
              ))}
            </MapContainer>

            {/* Legend */}
            <div className="absolute bottom-4 left-4 z-[1000] bg-white/95 backdrop-blur-sm rounded-lg px-3.5 py-2.5 shadow-md">
              <div className="flex items-center gap-2.5">
                <div className="w-5 h-5 rounded-full bg-[#1a3c2a] border-2 border-[#c9a962] flex items-center justify-center">
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#c9a962" strokeWidth="3"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/></svg>
                </div>
                <span className="font-montserrat text-[#1a3c2a] text-xs font-medium">Le Golf de Songon M'braté</span>
              </div>
            </div>
          </div>

          {/* Right: Travel Times */}
          <div className="bg-[#faf8f5] p-6 lg:p-8 overflow-y-auto">
            <h3 className="font-playfair text-2xl text-[#1a3c2a] mb-6">
              Temps de trajet
            </h3>

            <div className="space-y-1">
              {destinations.map((dest, index) => (
                <div
                  key={dest.id}
                  className={`group flex items-center gap-4 py-4 cursor-default transition-colors duration-200 ${
                    hoveredId === dest.id ? 'bg-[#1a3c2a]/[0.04] -mx-3 px-3 rounded-lg' : ''
                  } ${index < destinations.length - 1 ? 'border-b border-[#1a3c2a]/[0.06]' : ''}`}
                  onMouseEnter={() => setHoveredId(dest.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  data-testid={`travel-${dest.id}`}
                >
                  {/* Icon */}
                  <div className="w-10 h-10 rounded-xl bg-[#1a3c2a]/[0.05] flex items-center justify-center flex-shrink-0 group-hover:bg-[#1a3c2a]/[0.08] transition-colors">
                    <dest.icon className="w-4.5 h-4.5 text-[#1a3c2a]/50" strokeWidth={1.5} />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-playfair text-[#1a3c2a] text-[15px] leading-tight">
                      {dest.name}
                    </p>
                    <p className="font-montserrat text-[#1a3c2a]/35 text-[11px] mt-0.5">
                      {dest.route}
                    </p>
                  </div>

                  {/* Time */}
                  <span className="font-playfair text-[#1a3c2a] text-xl lg:text-2xl italic whitespace-nowrap">
                    {dest.time}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Nature Banner */}
        <div className="bg-[#1a3c2a]/50 backdrop-blur border border-[#c9a962]/10 rounded-2xl p-8 lg:p-10">
          <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-10">
            <div className="w-14 h-14 rounded-full bg-[#c9a962]/10 flex items-center justify-center flex-shrink-0">
              <TreePine className="w-7 h-7 text-[#c9a962]" />
            </div>
            <div className="text-center lg:text-left">
              <h3 className="font-playfair text-xl lg:text-2xl text-white mb-2">
                L'écrin naturel de <span className="text-[#c9a962] italic">Songon</span>
              </h3>
              <p className="font-montserrat text-white/45 text-sm leading-relaxed max-w-2xl">
                Loin de l'effervescence urbaine, le domaine s'épanouit au cœur d'un
                paysage de collines verdoyantes et de forêts préservées. Un cadre
                d'exception où le calme absolu et les grands espaces composent
                le quotidien des résidents.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GolfAccessibilite;
